import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/api';
import { TrendingUp, TrendingDown, Building2, Loader2, Calculator, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, Cell } from 'recharts';

export default function Benchmarking() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const res = await api.get('/analytics/benchmarking-summary');
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load benchmarking data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="text-[#3B82F6] animate-spin" size={28} />
      </div>
    );
  }

  const { customers = [], industry_average = 0.21, platform_average = 0.21 } = data || {};
  const fmt = (n) => `$${n?.toFixed(3) || '0.000'}`;

  // Chart data - top 10 customers by volume
  const chartData = customers
    .filter(c => c.cost_per_mile > 0)
    .slice(0, 10)
    .map(c => ({
      name: c.company_name?.substring(0, 12) + (c.company_name?.length > 12 ? '...' : ''),
      cpm: c.cost_per_mile,
      fullName: c.company_name,
      id: c.customer_id,
    }));

  const getBarColor = (cpm) => {
    if (cpm <= 0.15) return '#57EB31'; // Excellent
    if (cpm <= 0.21) return '#2DD4BF'; // Good
    if (cpm <= 0.28) return '#3B82F6'; // Average
    return '#FF6B63'; // Needs attention
  };

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    return (
      <div className="bg-[#1E293B] border border-[#2D3F5E] rounded-sm px-3 py-2 shadow-xl">
        <p className="text-[#E8EAED] font-['IBM_Plex_Sans'] text-sm font-bold mb-1">{d.fullName}</p>
        <p className="text-[#3B82F6] font-['JetBrains_Mono'] text-sm">
          ${d.cpm?.toFixed(3)}/mi
        </p>
      </div>
    );
  };

  return (
    <div className="p-6 space-y-6 animate-fade-in" data-testid="benchmarking-page">
      {/* Header */}
      <div className="flex items-center justify-between pb-4 border-b border-[#2D3F5E]/60">
        <div>
          <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-[#5C6A7A] mb-1">// FLEET ANALYTICS</p>
          <h1 className="text-white font-['Manrope'] font-extrabold text-2xl tracking-tight">Cost Benchmarking</h1>
        </div>
      </div>

      {/* Industry Benchmarks */}
      <div className="grid grid-cols-3 gap-4">
        <div className="card-industrial p-5 border-l-2 border-l-[#57EB31]">
          <span className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.12em] text-[#5C6A7A]">TOP PERFORMERS</span>
          <p className="text-3xl font-['JetBrains_Mono'] font-bold text-[#57EB31] mt-2">≤$0.150</p>
          <p className="text-xs text-[#8B9AAB] mt-1">per mile</p>
        </div>
        <div className="card-industrial p-5 border-l-2 border-l-[#3B82F6]">
          <span className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.12em] text-[#5C6A7A]">INDUSTRY AVERAGE</span>
          <p className="text-3xl font-['JetBrains_Mono'] font-bold text-[#3B82F6] mt-2">${industry_average?.toFixed(3)}</p>
          <p className="text-xs text-[#8B9AAB] mt-1">per mile</p>
        </div>
        <div className="card-industrial p-5 border-l-2 border-l-teal-400">
          <span className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.12em] text-[#5C6A7A]">PLATFORM AVERAGE</span>
          <p className="text-3xl font-['JetBrains_Mono'] font-bold text-teal-400 mt-2">${platform_average?.toFixed(3)}</p>
          <p className="text-xs text-[#8B9AAB] mt-1">your clients' average</p>
        </div>
      </div>

      {/* Chart */}
      <div className="card-industrial p-6">
        <h3 className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-[#5C6A7A] mb-4">// COST PER MILE BY CLIENT</h3>
        {chartData.length === 0 ? (
          <div className="h-64 flex items-center justify-center">
            <p className="text-[#5C6A7A] font-['IBM_Plex_Sans']">No data yet — seed demo data or wait for repairs</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={chartData} layout="vertical" margin={{ left: 80, right: 20 }}>
              <XAxis 
                type="number" 
                domain={[0, 0.35]} 
                tickFormatter={(v) => `$${v.toFixed(2)}`}
                axisLine={false} 
                tickLine={false} 
                tick={{ fill: '#5C6A7A', fontSize: 10, fontFamily: 'JetBrains Mono' }} 
              />
              <YAxis 
                type="category" 
                dataKey="name" 
                axisLine={false} 
                tickLine={false} 
                tick={{ fill: '#8B9AAB', fontSize: 11, fontFamily: 'IBM Plex Sans' }} 
                width={80}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(75,37,14,0.3)' }} />
              <ReferenceLine x={industry_average} stroke="#3B82F6" strokeDasharray="5 5" label={{ value: 'Industry Avg', fill: '#3B82F6', fontSize: 9 }} />
              <Bar dataKey="cpm" radius={[0, 4, 4, 0]}>
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={getBarColor(entry.cpm)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Customer List */}
      <div className="card-industrial overflow-hidden">
        <table className="w-full" data-testid="benchmarking-table">
          <thead>
            <tr className="border-b border-[#2D3F5E]">
              {['CLIENT', 'COST/MILE', 'TOTAL SPEND', 'FLEET MILES', 'TRUCKS', 'PERFORMANCE', ''].map((h, i) => (
                <th key={i} className="text-left px-4 py-3 text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A]">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {customers.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center py-12 text-[#5C6A7A] font-['IBM_Plex_Sans'] text-sm">
                  No customers with repair data yet
                </td>
              </tr>
            ) : customers.map((c, i) => {
              const perf = c.cost_per_mile <= 0.15 ? { label: 'Excellent', color: 'text-[#57EB31] bg-[#57EB31]/10 border-[#57EB31]/30' }
                : c.cost_per_mile <= 0.21 ? { label: 'Good', color: 'text-teal-400 bg-teal-900/20 border-teal-800/50' }
                : c.cost_per_mile <= 0.28 ? { label: 'Average', color: 'text-[#3B82F6] bg-[#3B82F6]/10 border-[#3B82F6]/30' }
                : c.cost_per_mile > 0 ? { label: 'Review', color: 'text-red-400 bg-red-900/20 border-red-800/50' }
                : { label: 'No Data', color: 'text-[#5C6A7A] bg-[#1E293B] border-[#2D3F5E]' };
              
              return (
                <tr 
                  key={c.customer_id} 
                  className="border-b border-[#2D3F5E]/30 hover:bg-[#2A1C14] transition-colors"
                  style={{ background: i % 2 !== 0 ? 'rgba(255,255,255,0.01)' : 'transparent' }}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Building2 size={14} className="text-[#2D3F5E]" />
                      <span className="text-sm text-white font-['IBM_Plex_Sans']">{c.company_name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`font-['JetBrains_Mono'] text-sm font-bold ${c.cost_per_mile <= industry_average ? 'text-[#57EB31]' : c.cost_per_mile > 0 ? 'text-[#3B82F6]' : 'text-[#5C6A7A]'}`}>
                      {c.cost_per_mile > 0 ? `$${c.cost_per_mile.toFixed(3)}` : '—'}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-['JetBrains_Mono'] text-xs text-[#8B9AAB]">
                    ${c.total_spend?.toLocaleString()}
                  </td>
                  <td className="px-4 py-3 font-['JetBrains_Mono'] text-xs text-[#8B9AAB]">
                    {c.total_miles?.toLocaleString()}
                  </td>
                  <td className="px-4 py-3 font-['JetBrains_Mono'] text-xs text-[#8B9AAB]">
                    {c.truck_count}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide border font-['JetBrains_Mono'] ${perf.color}`}>
                      {perf.label}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <button 
                      onClick={() => navigate(`/ops/analytics/roi/${c.customer_id}`)}
                      className="flex items-center gap-1 text-[#3B82F6] hover:underline text-xs font-['JetBrains_Mono']"
                    >
                      <Calculator size={12} />
                      ROI
                      <ChevronRight size={12} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-6 text-xs">
        {[
          { color: 'bg-[#57EB31]', label: 'Excellent (≤$0.15)' },
          { color: 'bg-teal-400', label: 'Good ($0.15-$0.21)' },
          { color: 'bg-[#3B82F6]', label: 'Average ($0.21-$0.28)' },
          { color: 'bg-red-400', label: 'Needs Review (>$0.28)' },
        ].map(({ color, label }) => (
          <div key={label} className="flex items-center gap-2">
            <div className={`w-3 h-3 rounded-full ${color}`} />
            <span className="text-[#8B9AAB] font-['IBM_Plex_Sans']">{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
